import React, { Fragment, useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { getProjectImageSrc, getSowDocumentUrl } from "../../../services/projectApi";

const FILE_SIZE_LIMIT = 2 * 1024 * 1024; // 2 MB
const SOW_SIZE_LIMIT = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const SOW_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const projectSchema = Yup.object().shape({
    clientId: Yup.string().required("Please select a client"),
    projectName: Yup.string()
        .min(3, "Project Name must be at least 3 characters")
        .max(100, "Project Name must not exceed 100 characters")
        .required("Project Name is required"),
    projectDescription: Yup.string()
        .max(300, "Description must not exceed 300 characters")
        .required("Project Description is required"),
    projectImage: Yup.mixed()
        .nullable()
        .test("fileType", "Only JPG or PNG images are allowed", (value) => {
            if (!value) return true;
            return ALLOWED_TYPES.includes(value.type);
        })
        .test("fileSize", "Image must be smaller than 2 MB", (value) => {
            if (!value) return true;
            return value.size <= FILE_SIZE_LIMIT;
        }),
    sowDocument: Yup.mixed()
        .nullable()
        .test("sowType", "Only PDF or Word (.doc, .docx) are allowed", (value) => {
            if (!value) return true;
            if (SOW_MIME.includes(value.type)) return true;
            if (!value.type && value.name) {
                const ext = value.name.split(".").pop()?.toLowerCase();
                return ["pdf", "doc", "docx"].includes(ext);
            }
            return false;
        })
        .test("sowSize", "SOW document must be smaller than 10 MB", (value) => {
            if (!value) return true;
            return value.size <= SOW_SIZE_LIMIT;
        }),
    isCompleted: Yup.boolean(),
    projectPercentageCompleted: Yup.number()
        .min(0, "Percentage must be between 0 and 100")
        .max(100, "Percentage must be between 0 and 100")
        .required("Completion percentage is required"),
    startDate: Yup.date()
        .typeError("Start Date must be a valid date")
        .required("Start Date is required"),
    endDate: Yup.date()
        .typeError("End Date must be a valid date")
        .min(Yup.ref("startDate"), "End Date must be after Start Date")
        .required("End Date is required"),
});

/* ── Progress bar colour helper ─────────────────────────── */
export const progressColor = (pct) => {
    if (pct === 100) return "success";
    if (pct >= 60) return "info";
    if (pct >= 30) return "warning";
    return "danger";
};

/* ── react-select custom styles (Bootstrap-compatible) ────── */
const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? "#6418c3" : "#ced4da",
        boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(100,24,195,.15)" : "none",
        "&:hover": { borderColor: "#6418c3" },
        minHeight: "calc(1.5em + 0.75rem + 2px)",
        fontSize: "0.875rem",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? "#6418c3"
            : state.isFocused
                ? "rgba(100,24,195,.08)"
                : "white",
        color: state.isSelected ? "#fff" : "#212529",
        fontSize: "0.875rem",
    }),
    placeholder: (base) => ({ ...base, color: "#6c757d" }),
    singleValue: (base) => ({ ...base, color: "#212529" }),
};

const ProjectForm = ({
    initialValues,
    onSubmit,
    onCancel,
    isEditMode,
    externalSubmitting,
    clients = [],
    clientsLoading = false,
    clientsFetchError = null,
    onAddClient,
}) => {
    const sowInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const [imagePreview, setImagePreview] = useState(() =>
        getProjectImageSrc(initialValues?.projectImage)
    );

    useEffect(() => {
        setImagePreview(getProjectImageSrc(initialValues?.projectImage));
    }, [initialValues?.projectImage]);

    const defaultValues = {
        clientId: "",
        projectName: "",
        projectDescription: "",
        isCompleted: false,
        projectPercentageCompleted: 0,
        startDate: "",
        endDate: "",
        ...initialValues,
        projectImage: null, // always start file input empty
        sowDocument: null,
    };

    const clientOptions = clients.map((c) => ({
        value: c._id,
        label: c.clientName,
    }));

    const handleImageChange = (e, setFieldValue, setFieldTouched) => {
        const file = e.currentTarget.files[0];
        setFieldTouched("projectImage", true);
        if (file) {
            setFieldValue("projectImage", file);
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSowChange = (e, setFieldValue, setFieldTouched) => {
        const file = e.currentTarget.files?.[0];
        setFieldTouched("sowDocument", true);
        if (file) {
            setFieldValue("sowDocument", file);
        } else {
            setFieldValue("sowDocument", null);
        }
    };

    const fieldClass = (touched, error) =>
        `form-group mb-3 ${touched ? (error ? "is-invalid" : "is-valid") : ""}`;

    const errBlock = (touched, error) =>
        touched && error ? (
            <div
                className="invalid-feedback animated fadeInUp"
                style={{ display: "block" }}
            >
                {error}
            </div>
        ) : null;

    return (
        <Fragment>
            <Formik
                initialValues={defaultValues}
                enableReinitialize
                validationSchema={projectSchema}
                onSubmit={onSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    resetForm,
                    setFieldValue,
                    setFieldTouched,
                }) => {
                    /* Auto-set percentage to 100 when isCompleted toggled on,
                       and auto-set isCompleted true when percentage reaches 100 */
                    const handleCompletedToggle = (e) => {
                        const checked = e.target.checked;
                        setFieldValue("isCompleted", checked);
                        if (checked) {
                            setFieldValue("projectPercentageCompleted", 100);
                        } else {
                            setFieldValue("projectPercentageCompleted", 0);
                        }
                    };

                    const handlePercentageChange = (e) => {
                        const val = Number(e.target.value);
                        setFieldValue("projectPercentageCompleted", val);
                        setFieldValue("isCompleted", val === 100);
                    };

                    const pct = Number(values.projectPercentageCompleted) || 0;
                    const barColor = progressColor(pct);
                    const selectedClient =
                        clientOptions.find((o) => o.value === values.clientId) || null;

                    return (
                        <form onSubmit={handleSubmit}>

                            {/* ── Full-width: Client Dropdown (FIRST FIELD) ── */}
                            <div className="row mb-1">
                                <div className="col-12">
                                    <div className={fieldClass(touched.clientId, errors.clientId)}>
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <label className="text-label mb-0">
                                                Client <span className="text-danger">*</span>
                                            </label>
                                            {onAddClient && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-xs"
                                                    onClick={onAddClient}
                                                    title="Add a new client"
                                                >
                                                    <i className="fa fa-plus me-1" />
                                                    Add Client
                                                </button>
                                            )}
                                        </div>

                                        {clientsFetchError ? (
                                            <div className="alert alert-warning py-2 mb-1">
                                                <i className="fa fa-exclamation-triangle me-1" />
                                                {clientsFetchError} — client list unavailable
                                            </div>
                                        ) : null}

                                        <Select
                                            inputId="clientId"
                                            options={clientOptions}
                                            value={selectedClient}
                                            onChange={(opt) => {
                                                setFieldValue("clientId", opt ? opt.value : "");
                                                setFieldTouched("clientId", true);
                                            }}
                                            onBlur={() => setFieldTouched("clientId", true)}
                                            placeholder={
                                                clientsLoading ? "Loading clients…" : "Select Client"
                                            }
                                            isLoading={clientsLoading}
                                            isDisabled={clientsLoading}
                                            isClearable
                                            styles={selectStyles}
                                            noOptionsMessage={() =>
                                                clientsFetchError
                                                    ? "Could not load clients"
                                                    : "No clients found"
                                            }
                                        />
                                        {errBlock(touched.clientId, errors.clientId)}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                {/* ── Left Column ── */}
                                <div className="col-xl-6 col-lg-6">

                                    {/* Project Name */}
                                    <div className={fieldClass(touched.projectName, errors.projectName)}>
                                        <label className="text-label">
                                            Project Name <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-folder-open" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="projectName"
                                                placeholder="Enter project name"
                                                value={values.projectName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {errBlock(touched.projectName, errors.projectName)}
                                        </div>
                                    </div>

                                    {/* Project Description */}
                                    <div className={fieldClass(touched.projectDescription, errors.projectDescription)}>
                                        <label className="text-label">
                                            Project Description <span className="text-danger">*</span>
                                            <small className="text-muted ms-1">(max 300 chars)</small>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            name="projectDescription"
                                            placeholder="Describe the project..."
                                            rows={4}
                                            value={values.projectDescription}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        <div className="d-flex justify-content-between mt-1">
                                            {errBlock(touched.projectDescription, errors.projectDescription)}
                                            <small
                                                className={`text-${values.projectDescription.length > 280 ? "danger" : "muted"
                                                    } ms-auto`}
                                            >
                                                {values.projectDescription.length}/300
                                            </small>
                                        </div>
                                    </div>

                                    {/* Project Image */}
                                    <div className={fieldClass(touched.projectImage, errors.projectImage)}>
                                        <label className="text-label">
                                            Project Image{" "}
                                            <small className="text-muted">(JPG / PNG, max 2 MB — optional)</small>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-image" />
                                            </span>
                                            <input
                                                ref={imageInputRef}
                                                type="file"
                                                className="form-control"
                                                accept="image/jpeg,image/jpg,image/png"
                                                onChange={(e) =>
                                                    handleImageChange(e, setFieldValue, setFieldTouched)
                                                }
                                            />
                                            {errBlock(touched.projectImage, errors.projectImage)}
                                        </div>
                                        {imagePreview && (
                                            <div className="mt-2 d-flex align-items-center gap-3">
                                                <img
                                                    src={getProjectImageSrc(imagePreview) || imagePreview}
                                                    alt="Preview"
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        objectFit: "cover",
                                                        borderRadius: "8px",
                                                        border: "2px solid #dee2e6",
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => {
                                                        setFieldValue("projectImage", null);
                                                        setImagePreview(
                                                            getProjectImageSrc(initialValues?.projectImage)
                                                        );
                                                        if (imageInputRef.current) imageInputRef.current.value = "";
                                                    }}
                                                >
                                                    <i className="fa fa-times me-1" /> Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* SOW document */}
                                    <div className={fieldClass(touched.sowDocument, errors.sowDocument)}>
                                        <label className="text-label">
                                            SOW document{" "}
                                            <small className="text-muted">
                                                (PDF / Word, max 10 MB — optional)
                                            </small>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-file-text-o" />
                                            </span>
                                            <input
                                                ref={sowInputRef}
                                                type="file"
                                                className="form-control"
                                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={(e) =>
                                                    handleSowChange(e, setFieldValue, setFieldTouched)
                                                }
                                            />
                                            {errBlock(touched.sowDocument, errors.sowDocument)}
                                        </div>
                                        {values.sowDocument instanceof File && (
                                            <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                                                <span className="text-muted small">
                                                    <i className="fa fa-paperclip me-1" />
                                                    {values.sowDocument.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => {
                                                        setFieldValue("sowDocument", null);
                                                        if (sowInputRef.current) sowInputRef.current.value = "";
                                                    }}
                                                >
                                                    <i className="fa fa-times me-1" /> Remove
                                                </button>
                                            </div>
                                        )}
                                        {!values.sowDocument &&
                                            initialValues?.sowDocument &&
                                            getSowDocumentUrl(initialValues.sowDocument) && (
                                                <div className="mt-2">
                                                    <a
                                                        href={getSowDocumentUrl(initialValues.sowDocument)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary"
                                                    >
                                                        <i className="fa fa-external-link me-1" />
                                                        View current SOW
                                                    </a>
                                                </div>
                                            )}
                                    </div>

                                    {/* Start Date */}
                                    <div className={fieldClass(touched.startDate, errors.startDate)}>
                                        <label className="text-label">
                                            Start Date <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-calendar" />
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="startDate"
                                                value={values.startDate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {errBlock(touched.startDate, errors.startDate)}
                                        </div>
                                    </div>

                                    {/* End Date */}
                                    <div className={fieldClass(touched.endDate, errors.endDate)}>
                                        <label className="text-label">
                                            End Date <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-calendar-check-o" />
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="endDate"
                                                value={values.endDate}
                                                min={values.startDate || undefined}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {errBlock(touched.endDate, errors.endDate)}
                                        </div>
                                    </div>

                                </div>

                                {/* ── Right Column ── */}
                                <div className="col-xl-6 col-lg-6">

                                    {/* Is Completed toggle */}
                                    <div className="form-group mb-3">
                                        <label className="text-label d-block">Completion Status</label>
                                        <div
                                            className={`d-flex align-items-center gap-3 p-3 rounded border ${values.isCompleted
                                                ? "border-success bg-success bg-opacity-10"
                                                : "border-warning bg-warning bg-opacity-10"
                                                }`}
                                        >
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="isCompleted"
                                                    name="isCompleted"
                                                    checked={values.isCompleted}
                                                    onChange={handleCompletedToggle}
                                                    style={{ width: "2.5rem", height: "1.25rem", cursor: "pointer" }}
                                                />
                                                <label
                                                    className={`form-check-label fw-semibold ms-2 mb-0  ${values.isCompleted ? "text-success" : "text-warning"
                                                        }`}
                                                    htmlFor="isCompleted"
                                                >
                                                    {values.isCompleted ? (
                                                        <>
                                                            <i className="fa fa-check-circle me-1" />
                                                            Completed
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa fa-clock-o me-1" />
                                                            In Progress
                                                        </>
                                                    )}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Percentage Completed */}
                                    <div
                                        className={fieldClass(
                                            touched.projectPercentageCompleted,
                                            errors.projectPercentageCompleted
                                        )}
                                    >
                                        <label className="text-label">
                                            Percentage Completed <span className="text-danger">*</span>
                                            {values.isCompleted && (
                                                <small className="text-muted ms-1">(auto-set to 100%)</small>
                                            )}
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-percent" />
                                            </span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="projectPercentageCompleted"
                                                placeholder="0 – 100"
                                                min={0}
                                                max={100}
                                                value={values.projectPercentageCompleted}
                                                onChange={handlePercentageChange}
                                                onBlur={handleBlur}
                                                disabled={values.isCompleted}
                                            />
                                            {errBlock(
                                                touched.projectPercentageCompleted,
                                                errors.projectPercentageCompleted
                                            )}
                                        </div>

                                        {/* Live preview progress bar */}
                                        <div className="mt-2">
                                            <div
                                                className="progress"
                                                style={{ height: "10px", borderRadius: "5px" }}
                                            >
                                                <div
                                                    className={`progress-bar bg-${barColor}`}
                                                    role="progressbar"
                                                    style={{
                                                        width: `${Math.min(100, Math.max(0, pct))}%`,
                                                        transition: "width 0.3s ease",
                                                    }}
                                                    aria-valuenow={pct}
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                />
                                            </div>
                                            <small className={`text-${barColor} mt-1 d-block`}>
                                                {pct}% complete
                                            </small>
                                        </div>
                                    </div>

                                    {/* Slider (range) */}
                                    <div className="form-group mb-4">
                                        <label className="text-label">Adjust with Slider</label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={values.projectPercentageCompleted}
                                            disabled={values.isCompleted}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                setFieldValue("projectPercentageCompleted", val);
                                                setFieldTouched("projectPercentageCompleted", true);
                                                setFieldValue("isCompleted", val === 100);
                                            }}
                                        />
                                        <div className="d-flex justify-content-between">
                                            <small className="text-muted">0%</small>
                                            <small className="text-muted">50%</small>
                                            <small className="text-muted">100%</small>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="form-group mb-0 d-flex gap-2 flex-wrap">
                                        <button
                                            type="submit"
                                            className="btn btn-primary py-2"
                                            disabled={isSubmitting || externalSubmitting}
                                        >
                                            {isSubmitting || externalSubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    {isEditMode ? "Updating..." : "Saving..."}
                                                </>
                                            ) : isEditMode ? (
                                                "Update Project"
                                            ) : (
                                                "Add Project"
                                            )}
                                        </button>
                                        {!isEditMode && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary py-2"
                                                onClick={() => {
                                                    resetForm();
                                                    setImagePreview(getProjectImageSrc(initialValues?.projectImage));
                                                    if (sowInputRef.current) sowInputRef.current.value = "";
                                                    if (imageInputRef.current) imageInputRef.current.value = "";
                                                }}
                                            >
                                                Reset
                                            </button>
                                        )}
                                        {onCancel && (
                                            <button
                                                type="button"
                                                className="btn btn-light py-2"
                                                onClick={onCancel}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </Fragment>
    );
};

export default ProjectForm;
